import React, { useState } from "react";
import theme from "../../theme";
import deviceImage from "../../assets/laptopPreview.png";
import logoImage from "../../assets/LogoImage.png";
import { useNavigate } from "react-router-dom";
import SC from "./style";
import { Checkbox } from "@mui/material";
import axios from "axios";
import CryptoJS from "crypto-js";
import { CheckCircle } from "lucide-react";
import { Eye, EyeOff } from "lucide-react"; // For toggle icon

const WelcomePage = () => {
  const [accessCode, setAccessCode] = useState("");
  const [agreedToNonDisclosure, setAgreedToNonDisclosure] = useState(false);
  const [agreedToTrialTerms, setAgreedToTrialTerms] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [isVerified, setVerified] = useState(false);
  const navigate = useNavigate();

  const [showAccessCode, setShowAccessCode] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();

    if (!accessCode.trim()) {
      setError("Access code cannot be empty.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await axios.post(
        `api/rest/users/trial/access-tokens`,
        { trial_token: accessCode.trim() },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: process.env.REACT_APP_API_KEY,
          },
        },
      );

      if (response.status == 200) {
        const secretKey = "my-very-secret-key";
        const encrypted = CryptoJS.AES.encrypt("true", secretKey).toString();
        sessionStorage.setItem("isAuthorized", encrypted);

        setVerified(true);
        setLoading(false);

        // Navigate after 5 seconds
        setTimeout(() => {
          navigate("/platform-overview");
        }, 3000);
      } else {
        throw new Error("Invalid response from server.");
      }
    } catch (error) {
      console.error("Error:", error);
      setError("Invalid trial token. Please try again.");
      setLoading(false);
    }
  };

  const handleClick = () => {
    navigate("/letconnect");
  };

  const handleNavigate = () => {
    navigate("/");
  };

  const isFormValid =
    accessCode.trim() && agreedToNonDisclosure && agreedToTrialTerms;

  return (
    <SC.PageWrapper>
      <SC.TopBar>
        <img
          src={logoImage}
          alt="Company Logo"
          loading="lazy"
          onClick={handleNavigate}
          style={{ cursor: "pointer" }}
        />
      </SC.TopBar>

      <SC.MainContent>
        <SC.ImageWrapper>
          <img
            src={deviceImage}
            alt="Device showcase"
            loading="lazy"
            // width={"800px"}
          />
        </SC.ImageWrapper>

        <SC.FormWrapper>
          <SC.Card>
            <SC.HeaderContainer>
              <SC.Heading>Welcome!</SC.Heading>
              <SC.SubText>
                Manage Projects, Monitor Progress, and Drive Success
              </SC.SubText>
            </SC.HeaderContainer>

            <SC.Form onSubmit={handleVerify}>
              <SC.InputWrapper>
                <SC.Label htmlFor="accessCode">Enter Access Code</SC.Label>
                <div style={{ position: "relative" }}>
                  <SC.InputField
                    type={showAccessCode ? "text" : "password"}
                    id="accessCode"
                    name="accessCode"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    required
                    aria-required="true"
                  />
                  <div
                    onClick={() => setShowAccessCode(!showAccessCode)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: theme.colors.primary,
                    }}
                  >
                    {showAccessCode ? <EyeOff size={18} /> : <Eye size={18} />}
                  </div>
                </div>
              </SC.InputWrapper>

              <SC.SmallDivider />

              <SC.CheckboxContainer>
                <SC.CheckboxItem>
                  <label style={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={agreedToNonDisclosure}
                      onChange={(e) =>
                        setAgreedToNonDisclosure(e.target.checked)
                      }
                      sx={{
                        color: theme.colors.text.primary,
                        "&.Mui-checked": { color: theme.colors.primary },
                      }}
                      required
                      size="small"
                      inputProps={{ "aria-label": "Agree to non-disclosure" }}
                    />
                    <SC.CheckboxText>
                      I agree not to disclose, share, or reproduce the Prakalpa
                      proprietary information (including and not limited to the
                      concept, design and flow of the software) and any
                      confidential information accessed during the trial.
                    </SC.CheckboxText>
                  </label>
                </SC.CheckboxItem>

                <SC.CheckboxItem>
                  <label style={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={agreedToTrialTerms}
                      onChange={(e) => setAgreedToTrialTerms(e.target.checked)}
                      sx={{
                        color: theme.colors.text.primary,
                        "&.Mui-checked": { color: theme.colors.primary },
                      }}
                      required
                      size="small"
                      inputProps={{ "aria-label": "Agree to trial terms" }}
                    />
                    <SC.CheckboxText>
                      This trial is provided "as is" without any warranties or
                      guarantees. Prakalpa is not responsible for any data loss
                      or service issues during the trial period.
                    </SC.CheckboxText>
                  </label>
                </SC.CheckboxItem>
              </SC.CheckboxContainer>

              <SC.Button
                type="submit"
                disabled={!isFormValid || isLoading || isVerified}
              >
                {isLoading
                  ? "Verifying..."
                  : isVerified
                    ? "Verified!"
                    : "Verify"}
              </SC.Button>

              {isVerified && (
                <div
                  style={{
                    marginTop: "10px",
                    color: theme.colors.primary,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <CheckCircle size={20} />{" "}
                  <SC.Span>Verified Successfully! Redirecting...</SC.Span>
                </div>
              )}

              {error && <span style={{ color: "red" }}>{error}</span>}

              <SC.Divider>
                <span>OR</span>
              </SC.Divider>

              <SC.AlternativeAction>
                <span>Don't have access code?</span>
                <SC.AlternativeButton type="button" onClick={handleClick}>
                  <span>Let's Connect</span>
                </SC.AlternativeButton>
              </SC.AlternativeAction>
            </SC.Form>
          </SC.Card>
        </SC.FormWrapper>
      </SC.MainContent>
    </SC.PageWrapper>
  );
};

export default WelcomePage;
